/**
 * Shared arrow-decorator mesh builder (S9b).
 *
 * Both the DIMENSION arrow / tick heads (`scene/sceneDimensionMeshFactories.ts`)
 * and the MULTILEADER arrow heads (`scene/sceneMultileaderMeshFactories.ts`)
 * materialise the same way: group the placements by `(variant, colorHex)`,
 * then emit ONE `THREE.InstancedMesh` per group for FILLED arrow prototypes
 * (triangle draw path) or ONE `THREE.LineSegments` per instance for OUTLINE
 * prototypes (gl.LINES draw path — `InstancedMesh` ships triangle topology and
 * cannot render the line-pair outline prototypes; codex P1 on PR #63).
 *
 * Extracting the materialiser here is the ONE geometry truth both factories
 * call (jscpd duplicate gate) — the only per-caller difference is the source
 * primitive kinds (dimension has `arrow` + `tick`; multileader has `arrow`)
 * and the `userData.kind` tag, both handled by the caller mapping its
 * primitives into {@link ArrowInstanceInput}s and passing a `meshKind`.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1
 *
 * `none`-variant placements are skipped (empty prototype geometry — no draw);
 * every `(variant, colorHex)` group is honoured even when both ends share a
 * variant + color; filled variants keep InstancedMesh batching while outline
 * variants fall back to per-instance LineSegments (no simplification into a
 * single draw path).
 */

import * as THREE from "three";

import type { Mat2 } from "@dxf-ai-editor/cad-core";

import type { ArrowVariant, Vec3 } from "../types";
import type { DirectMeshLayerStamp } from "../layerChainVisibility";
import { getArrowPrototype, arrowRotationFromDirection } from "./arrowMarkerMesh";

/**
 * A materialised arrow decorator: filled arrowheads batch into one
 * `THREE.InstancedMesh` per `(variant, colorHex)` group; outline arrowheads
 * (`_OPEN`, `_OPEN30`, `_CLOSEDBLANK`, `_DOTBLANK`) materialise as one
 * `THREE.LineSegments` per instance.
 */
export type ArrowDecoratorObject3D = THREE.InstancedMesh | THREE.LineSegments;

/** One resolved arrow-head placement (variant + pose + color). */
export interface ArrowInstanceInput {
  /** Tip of the arrow (where the leader / dim line begins). */
  tip: Vec3;
  /** Direction the arrow points (unit vector). */
  direction: Vec3;
  /** World-space arrow size (the ALONG-direction length; half-width `size/2`). */
  size: number;
  variant: ArrowVariant;
  colorHex: string;
  /**
   * OPTIONAL exact 2×2 linear map from unit-prototype space to world, applied
   * about {@link tip} — see the `arrow` primitive's own `glyphLinear` doc in
   * `../types.ts` (Codex PR #784 round 11 P2). When present it REPLACES the
   * `Rz(direction) · S(size, size)` pose below, because a block INSERT's
   * linear part can stretch the along-direction and the perpendicular by
   * different factors and shear a diagonal arrowhead — none of which a unit
   * direction plus one scalar can express.
   *
   * The `MultileaderPrimitive` / `DimensionPrimitive` `arrow` shapes are
   * structural supersets of this interface (the multileader factory pushes
   * primitives straight in), so this field arrives on the same spread.
   */
  glyphLinear?: Mat2;
}

interface ArrowInstancePose {
  tip: Vec3;
  direction: Vec3;
  size: number;
  glyphLinear?: Mat2;
}

/**
 * `T(tip) · L` for a placement that carries a full 2×2 glyph map — the map's
 * columns are the world images of the prototype's own X / Y axes, so a
 * non-uniform or shearing enclosing INSERT reaches the GPU intact.
 */
function glyphMatrix(
  out: THREE.Matrix4,
  tip: Vec3,
  m: Mat2,
): THREE.Matrix4 {
  // `Matrix4.set` is ROW-major; the prototype is planar (z = 0), so the third
  // row/column stays the identity and only the translation carries `tip.z`.
  return out.set(
    m.m00, m.m01, 0, tip.x,
    m.m10, m.m11, 0, tip.y,
    0, 0, 1, tip.z,
    0, 0, 0, 1,
  );
}

interface ArrowGroup {
  variant: ArrowVariant;
  colorHex: string;
  fill: boolean;
  instances: ArrowInstancePose[];
}

/**
 * Materialise `instances` into batched / per-instance THREE objects. `meshKind`
 * is stamped onto each object's `userData.kind` (`"dimension-arrow"` /
 * `"multileader-arrow"`) alongside `userData.entityId` so dispose + hit-test
 * can attribute them to the source entity. Returns `[]` when every placement
 * is a `none` variant (nothing to draw).
 */
export function buildArrowInstanceMeshes(
  instances: readonly ArrowInstanceInput[],
  entityId: string,
  meshKind: string,
  // Codex PR #784 P1 — the owning renderable's OWN layer (+ its
  // block-reference layer chain, round 3), stamped as `userData.entityLayer`
  // / `userData.parentLayers` (`directMeshLayerStamp`) so the per-viewport
  // frozen-layer gate (`rendering/viewportRenderPass.ts
  // isDirectMeshOnFrozenLayer`) hides a satellite child's arrows by the
  // CHILD's layer AND its INSERT's while `entityId` stays the parent's for
  // the visible-set mask.
  layerStamp: DirectMeshLayerStamp,
): ArrowDecoratorObject3D[] {
  // Group instances by (variant, colorHex). Group key avoids JSON overhead —
  // variants are a closed enum string, colorHex is a hex string already.
  const groupMap = new Map<string, ArrowGroup>();
  for (const inst of instances) {
    // Skip `none` — the prototype geometry is empty and produces no draw.
    if (inst.variant === "none") continue;
    const key = `${inst.variant}|${inst.colorHex}`;
    let g = groupMap.get(key);
    if (!g) {
      const proto = getArrowPrototype(inst.variant);
      g = {
        variant: inst.variant,
        colorHex: inst.colorHex,
        fill: proto.fill,
        instances: [],
      };
      groupMap.set(key, g);
    }
    g.instances.push({
      tip: inst.tip,
      direction: inst.direction,
      size: inst.size,
      ...(inst.glyphLinear ? { glyphLinear: inst.glyphLinear } : {}),
    });
  }

  if (groupMap.size === 0) return [];

  const out: ArrowDecoratorObject3D[] = [];
  const tmpMatrix = new THREE.Matrix4();
  const tmpTranslate = new THREE.Matrix4();
  const tmpRotate = new THREE.Matrix4();
  const tmpScale = new THREE.Matrix4();

  for (const group of groupMap.values()) {
    const proto = getArrowPrototype(group.variant);
    const color = new THREE.Color(group.colorHex);

    if (group.fill) {
      // Filled variant — triangle prototype, MeshBasicMaterial. Use
      // InstancedMesh batching: one mesh per group, per-instance matrix
      // encodes pose + scale.
      const material = new THREE.MeshBasicMaterial({
        color,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.InstancedMesh(
        proto.geometry,
        material,
        group.instances.length,
      );
      mesh.userData = {
        entityId,
        ...layerStamp,
        kind: meshKind,
        variant: group.variant,
      };
      mesh.renderOrder = 1;
      mesh.frustumCulled = false;
      for (let i = 0; i < group.instances.length; i++) {
        const inst = group.instances[i]!;
        if (inst.glyphLinear) {
          glyphMatrix(tmpMatrix, inst.tip, inst.glyphLinear);
        } else {
          const rot = arrowRotationFromDirection(inst.direction);
          tmpTranslate.makeTranslation(inst.tip.x, inst.tip.y, inst.tip.z);
          tmpRotate.makeRotationZ(rot);
          tmpScale.makeScale(inst.size, inst.size, 1);
          tmpMatrix.copy(tmpTranslate).multiply(tmpRotate).multiply(tmpScale);
        }
        mesh.setMatrixAt(i, tmpMatrix);
      }
      mesh.instanceMatrix.needsUpdate = true;
      out.push(mesh);
    } else {
      // Outline variant — line-pair prototype geometry. `InstancedMesh` is a
      // triangle draw path so it cannot render line topology (codex P1 on
      // PR #63) — emit one non-instanced `THREE.LineSegments` per instance.
      const material = new THREE.LineBasicMaterial({ color });
      for (const inst of group.instances) {
        const mesh = new THREE.LineSegments(proto.geometry, material);
        mesh.userData = {
          entityId,
          ...layerStamp,
          kind: meshKind,
          variant: group.variant,
        };
        mesh.renderOrder = 1;
        mesh.frustumCulled = false;
        mesh.position.set(inst.tip.x, inst.tip.y, inst.tip.z);
        if (inst.glyphLinear) {
          // A sheared / non-uniform glyph map is NOT decomposable into
          // position + rotation + uniform scale, so the local matrix is set
          // directly and three.js is told to stop recomposing it from the TRS
          // triple (`updateMatrixWorld` would otherwise overwrite it on the
          // next frame). `position` is still the tip, so consumers that read
          // it (dispose / attribution walks) see the same placement.
          mesh.matrixAutoUpdate = false;
          glyphMatrix(mesh.matrix, inst.tip, inst.glyphLinear);
        } else {
          mesh.rotation.z = arrowRotationFromDirection(inst.direction);
          mesh.scale.set(inst.size, inst.size, 1);
          mesh.updateMatrix();
        }
        out.push(mesh);
      }
    }
  }
  return out;
}
